import {
  requestOpenapiHydrationCacheAdd,
  requestOpenapiHydrationCacheClear,
  requestOpenapiHydrationCacheHas,
  requestOpenapiHydrationCacheRemove,
  sendOpenapiHydrationCacheResponse,
} from './actions'
import { listenMessage } from '@wellesley/host-remote/common/message/listenMessage'
import { sendMessage } from '@wellesley/host-remote/common/message/sendMessage'
import genId from 'helpers/genId'
import { isInIframe } from 'helpers/isInIframe'

type RequestPayload = { id: string; key: string }

const isCrossOriginIframe = () => {
  try {
    if (!isInIframe()) return false
    return window.location.origin !== window.parent.location.origin
  } catch {
    return true
  }
}

const getSharedSameOriginSet = () => {
  const parent = window.parent as unknown as {
    __wlsy_openapiHydrationCache?: Set<string>
  }

  if (!parent.__wlsy_openapiHydrationCache) {
    parent.__wlsy_openapiHydrationCache = new Set<string>()
  }

  return parent.__wlsy_openapiHydrationCache
}

const localSet = new Set<string>()

export const remoteOpenapiHydrationCache = (): {
  has: (key: string) => Promise<boolean>
  add: (key: string) => Promise<void>
  remove: (key: string) => Promise<void>
  clear: () => Promise<void>
} => {
  const isRemote = isCrossOriginIframe()

  const pending = new Map<
    string,
    {
      resolve: (value: boolean | null) => void
      reject: (error: Error) => void
      timeoutId: number
    }
  >()

  const ensureRemoteListener = (() => {
    let unsubscribe: null | (() => void) = null

    return () => {
      if (!isRemote || unsubscribe) return

      unsubscribe = listenMessage({
        sourceWindow: window.parent,
        onMessage: (action) => {
          if (!sendOpenapiHydrationCacheResponse.match(action)) return

          const { id, value, success, error } = (
            action as unknown as ReturnType<
              typeof sendOpenapiHydrationCacheResponse
            >
          ).payload
          const entry = pending.get(id)
          if (!entry) return

          pending.delete(id)
          window.clearTimeout(entry.timeoutId)

          if (success) entry.resolve(value)
          else entry.reject(new Error(error || 'Unknown error'))
        },
        whitelist: [sendOpenapiHydrationCacheResponse],
      })
    }
  })()

  const sendRequest = (action: {
    type: string
    payload: Record<string, unknown>
  }) => {
    ensureRemoteListener()

    sendMessage({
      action,
      windowElement: window.parent,
      whitelist: [
        requestOpenapiHydrationCacheHas,
        requestOpenapiHydrationCacheAdd,
        requestOpenapiHydrationCacheRemove,
        requestOpenapiHydrationCacheClear,
      ],
    })
  }

  const createRequest = <P extends { id: string }>(
    actionCreator: (payload: P) => { type: string; payload: P },
    payload: Omit<P, 'id'>,
  ): Promise<boolean | null> => {
    const id = genId()

    return new Promise((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        pending.delete(id)
        reject(new Error('OpenAPI hydration cache request timed out'))
      }, 8000)

      pending.set(id, {
        resolve,
        reject,
        timeoutId,
      })

      sendRequest(actionCreator({ id, ...payload } as P))
    })
  }

  const hasLocal = (key: string) => {
    if (isInIframe() && !isRemote) {
      return getSharedSameOriginSet().has(key)
    }

    return localSet.has(key)
  }

  const addLocal = (key: string) => {
    if (isInIframe() && !isRemote) {
      getSharedSameOriginSet().add(key)
      return
    }

    localSet.add(key)
  }

  const removeLocal = (key: string) => {
    if (isInIframe() && !isRemote) {
      getSharedSameOriginSet().delete(key)
      return
    }

    localSet.delete(key)
  }

  const clearLocal = () => {
    if (isInIframe() && !isRemote) {
      getSharedSameOriginSet().clear()
      return
    }

    localSet.clear()
  }

  const has = async (key: string) => {
    if (!isRemote) return hasLocal(key)

    const result = await createRequest<RequestPayload>(
      requestOpenapiHydrationCacheHas,
      { key },
    )
    return !!result
  }

  const add = async (key: string) => {
    if (!isRemote) {
      addLocal(key)
      return
    }

    await createRequest<RequestPayload>(requestOpenapiHydrationCacheAdd, {
      key,
    })
  }

  const remove = async (key: string) => {
    if (!isRemote) {
      removeLocal(key)
      return
    }

    await createRequest<RequestPayload>(requestOpenapiHydrationCacheRemove, {
      key,
    })
  }

  const clear = async () => {
    if (!isRemote) {
      clearLocal()
      return
    }

    await createRequest<{ id: string }>(requestOpenapiHydrationCacheClear, {})
  }

  return { has, add, remove, clear }
}
